import { CURRICULUM } from "training/curriculum";
import { MODULES } from "training/curriculum/modules";
import { ITourStep, TourModule } from "training/types";
import { IQuizAnswer, ITrainingProgress } from "types/Training";

export { CERTIFICATE_PATH, QUIZ_PATH } from "training/paths";

/** CLE-3015 PR 4 — the small rules the knowledge check, the certificate and the banner share. */

/** The tour's last card; its "Start the knowledge check" button opens the quiz. */
export const WALKTHROUGH_DONE_STEP_ID = "m6.07b-walkthrough-done";

/**
 * The tour is behind the trainee and the knowledge check is what is left: the tour finished on its last
 * card (`currentStepId` cleared, the card completed), or they have already tried the quiz once.
 */
export function isReadyForQuiz(
  progress: ITrainingProgress | null | undefined
): boolean {
  if (!progress) return false;
  if ((progress.quiz?.attempts ?? 0) > 0) return true;
  return (
    !progress.currentStepId &&
    (progress.completed ?? []).includes(WALKTHROUGH_DONE_STEP_ID)
  );
}

export interface IWhereItLives {
  module: TourModule;
  moduleTitle: string;
  /** The step's on-screen label, or the first sentence of its text. */
  what: string;
}

/** The first sentence of the step's first paragraph. */
export function firstSentence(text: string): string {
  const paragraph = text.split("\n").find((line) => line.trim()) ?? "";
  const match = /^.*?[.!?](?=\s|$)/.exec(paragraph.trim());
  return (match ? match[0] : paragraph).trim();
}

/** "Where it lives" for a wrong answer: the curriculum step the API names, or null for an unknown id. */
export function whereItLives(
  stepId: string,
  steps: ITourStep[] = CURRICULUM
): IWhereItLives | null {
  const step = steps.find((s) => s.id === stepId);
  if (!step) return null;
  return {
    module: step.module,
    moduleTitle: MODULES[step.module].title,
    what: step.label ?? firstSentence(step.text),
  };
}

/**
 * "Try again": keep the answers that were right, clear the wrong ones. Returns the answers to keep,
 * keyed by question id.
 */
export function keepRightAnswers(
  selected: Record<string, number>,
  wrongIds: string[]
): Record<string, number> {
  const kept = { ...selected };
  wrongIds.forEach((id) => {
    delete kept[id];
  });
  return kept;
}

export function toAnswers(selected: Record<string, number>): IQuizAnswer[] {
  return Object.entries(selected).map(([questionId, choiceIndex]) => ({
    questionId,
    choiceIndex,
  }));
}
